import Loading from '@/components/Loading';
import SelectDropDown from '@/components/UI/SelectDropDown/SelectDropDown';
import Switch from '@/components/UI/FormElements/Switch';
import { errorToast, successToast } from '@/helpers.tsx';
import { _isNil, _isNotBlank, _isNotEmpty, _notNil } from '@/littledash.ts';
import InVivoError from '@/model/InVivoError.ts';
import { type MetadataGlossaryApiId } from '@/model/Metadata.model.ts';
import {
  type SampleMetaOptionV1,
  type SampleNameTemplateApiId,
  type SampleNameTokenDefinitionV1,
} from '@/model/SampleNameTemplate.model.ts';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal.tsx';
import { type FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TemplatePreview } from './TemplatePreview.tsx';
import { TemplateSequenceEditor, type SequenceToken } from './TemplateSequenceEditor.tsx';
import styles from './SampleNameTemplateBuilder.module.scss';

const newLocalId = () => Math.random().toString(36);

const DEFAULT_TOKEN_ORDER = ['study_code', 'sample_type', 'group_number', 'animal_id', 'timepoint'];

const ANCHOR_TOKEN_VALUES = new Set(['study_code', 'sample_type', 'animal_id']);

const hasAnchorToken = (seq: Array<SequenceToken>) =>
  seq.some(
    (t) =>
      _notNil(t.customValue) ||
      (t.definition.type === 'token' && ANCHOR_TOKEN_VALUES.has(t.definition.value) && _isNil(t.customValue))
  );

const hasStudyCodeToken = (seq: Array<SequenceToken>) =>
  seq.some((t) => _isNil(t.customValue) && t.definition.type === 'token' && t.definition.value === 'study_code');

interface TokenMenuProps {
  definitions: Array<SampleNameTokenDefinitionV1>;
  usedDefinitionIds: Set<number>;
  usedMetaGlossaryIds: Set<MetadataGlossaryApiId>;
  onAdd: (definition: SampleNameTokenDefinitionV1) => void;
  onAddMeta: (definition: SampleNameTokenDefinitionV1, option: SampleMetaOptionV1) => void;
  onAddFreeText: (value: string) => void;
  closeMenu?: () => void;
}

const TokenMenu: FC<TokenMenuProps> = ({
  definitions,
  usedDefinitionIds,
  usedMetaGlossaryIds,
  onAdd,
  onAddMeta,
  onAddFreeText,
  closeMenu,
}) => {
  const [freeTextValue, setFreeTextValue] = useState<string | null>(null);

  const tokens = definitions.filter((d) => d.type === 'token' && d.value !== 'sample_meta');
  const sampleMetaDef = definitions.find((d) => d.type === 'token' && d.value === 'sample_meta');
  const metaOptions: Array<SampleMetaOptionV1> = Array.isArray(sampleMetaDef?.meta_options)
    ? (sampleMetaDef.meta_options as Array<SampleMetaOptionV1>)
    : [];

  const handleAdd = (def: SampleNameTokenDefinitionV1) => {
    onAdd(def);
    closeMenu?.();
  };

  const handleFreeTextCommit = () => {
    if (_isNotBlank(freeTextValue)) {
      onAddFreeText(freeTextValue.trim());
      setFreeTextValue(null);
      closeMenu?.();
    }
  };

  const handleAddMeta = (option: SampleMetaOptionV1) => {
    if (_notNil(sampleMetaDef)) {
      onAddMeta(sampleMetaDef, option);
    }
    closeMenu?.();
  };

  const handleItemKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  return (
    <ul role="listbox" className={styles.tokenMenuList}>
      {tokens.map((def) => {
        const disabled = usedDefinitionIds.has(def.id);
        return (
          <li
            key={def.id}
            role="option"
            aria-selected={false}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            className={`${styles.dropdownItem}${disabled ? ` ${styles.dropdownItemDisabled}` : ''}`}
            onClick={() => !disabled && handleAdd(def)}
            onKeyDown={(e) => !disabled && handleItemKeyDown(e, () => handleAdd(def))}
          >
            {def.label}
          </li>
        );
      })}
      {_isNil(freeTextValue) ? (
        <li
          role="option"
          aria-selected={false}
          tabIndex={0}
          className={styles.dropdownItem}
          onClick={() => setFreeTextValue('')}
          onKeyDown={(e) => handleItemKeyDown(e, () => setFreeTextValue(''))}
        >
          Custom text…
        </li>
      ) : (
        <li role="presentation" className={styles.freeTextItem}>
          <input
            type="text"
            autoFocus
            value={freeTextValue}
            maxLength={100}
            placeholder="Enter custom text"
            onChange={(e) => setFreeTextValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleFreeTextCommit();
              }
              if (e.key === 'Escape') {
                setFreeTextValue(null);
              }
            }}
          />
        </li>
      )}
      {metaOptions.length > 0 && (
        <>
          <li className={styles.dropdownSectionTitle} role="presentation">
            Sample metadata
          </li>
          {metaOptions.map((option) => {
            const disabled = usedMetaGlossaryIds.has(option.api_id);
            return (
              <li
                key={option.api_id}
                role="option"
                aria-selected={false}
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : 0}
                className={`${styles.dropdownItem}${disabled ? ` ${styles.dropdownItemDisabled}` : ''}`}
                onClick={() => !disabled && handleAddMeta(option)}
                onKeyDown={(e) => !disabled && handleItemKeyDown(e, () => handleAddMeta(option))}
              >
                {option.title}
              </li>
            );
          })}
        </>
      )}
    </ul>
  );
};

export interface SampleNameTemplateBuilderModalProps {
  closeModal: () => void;
  templateApiId?: SampleNameTemplateApiId;
  readOnly?: boolean;
  onSuccess?: () => void;
}

export const SampleNameTemplateBuilderModal: FC<SampleNameTemplateBuilderModalProps> = ({
  closeModal,
  templateApiId,
  readOnly = false,
  onSuccess,
}) => {
  const isEdit = _notNil(templateApiId);

  const [userSequence, setUserSequence] = useState<Array<SequenceToken> | null>(null);
  const [saving, setSaving] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [nameError, setNameError] = useState('');
  const [tokensError, setTokensError] = useState('');
  const [autoFill, setAutoFill] = useState(true);

  const { response: definitionsResponse, loading: definitionsLoading } = useApiHook({
    endpoint: 'GET /api/v1/team/sample-name-templates/tokens',
    options: {
      onError: { throw: false, capture: true, toast: true, slug: 'template-builder-load-defs' },
    },
  });

  const { response: studyCodesResponse } = useApiHook({
    endpoint: 'GET /api/v1/team/study-codes',
    query: { perPage: 5 },
    options: {
      onError: { throw: false, capture: true, toast: false, slug: 'template-builder-load-study-codes' },
    },
  });

  const hasStudyCodes = studyCodesResponse?.type === 'success' && (studyCodesResponse.body?.data?.length ?? 0) > 0;

  const { invoke: loadTemplate, loading: templateLoading } = useApiHook({
    endpoint: 'GET /api/v1/team/sample-name-templates/{templateApiId}',
    options: {
      onError: { throw: false, capture: true, toast: true, slug: 'template-builder-load' },
      invokeOnInit: false,
    },
  });

  const { invoke: createTemplate } = useApiHook({
    endpoint: 'POST /api/v1/team/sample-name-templates',
    options: { onError: { throw: true, capture: false, toast: false }, invokeOnInit: false },
  });

  const { invoke: updateTemplate } = useApiHook({
    endpoint: 'POST /api/v1/team/sample-name-templates/{templateApiId}',
    options: { onError: { throw: true, capture: false, toast: false }, invokeOnInit: false },
  });

  const definitions: Array<SampleNameTokenDefinitionV1> = useMemo(
    () => (definitionsResponse?.type === 'success' ? definitionsResponse.body : []),
    [definitionsResponse]
  );

  const separators = useMemo(() => definitions.filter((d) => d.type === 'separator'), [definitions]);

  useEffect(() => {
    if (!isEdit || _isNil(templateApiId)) {
      return;
    }
    loadTemplate({ path: { templateApiId } }).then((response) => {
      if (response.type === 'success') {
        const template = response.body;
        setTemplateName(template.name ?? '');
        const sorted = [...template.tokens].sort((a, b) => a.position - b.position);
        setUserSequence(
          sorted.map((t) => ({
            localId: newLocalId(),
            definition: t.definition,
            customValue: typeof t.custom_value === 'string' ? t.custom_value : undefined,
            metadataGlossaryApiId:
              _notNil(t.metadata_glossary_api_id) && typeof t.metadata_glossary_api_id === 'string'
                ? t.metadata_glossary_api_id
                : undefined,
            metadataGlossaryTitle:
              _notNil(t.metadata_glossary_title) && typeof t.metadata_glossary_title === 'string'
                ? t.metadata_glossary_title
                : undefined,
            animalIdPrimary: t.animal_id_primary ?? undefined,
            animalIdSecondary: t.animal_id_secondary ?? undefined,
          }))
        );
      }
    });
  }, [isEdit, templateApiId]);

  const defaultSequence = useMemo<Array<SequenceToken>>(() => {
    if (isEdit || !_isNotEmpty(definitions)) {
      return [];
    }
    const tokens = DEFAULT_TOKEN_ORDER.reduce<Array<SampleNameTokenDefinitionV1>>((acc, value) => {
      const def = definitions.find((d) => d.type === 'token' && d.value === value);
      if (_notNil(def)) {
        acc.push(def);
      }
      return acc;
    }, []);
    const dashSeparator = definitions.find((d) => d.type === 'separator' && d.value === '-');
    return tokens.reduce<Array<SequenceToken>>((acc, tokenDef, i) => {
      acc.push({ localId: newLocalId(), definition: tokenDef });
      if (i < tokens.length - 1 && _notNil(dashSeparator)) {
        acc.push({ localId: newLocalId(), definition: dashSeparator });
      }
      return acc;
    }, []);
  }, [isEdit, definitions]);

  const sequence = userSequence ?? defaultSequence;
  const sequenceRef = useRef<Array<SequenceToken>>(sequence);
  useLayoutEffect(() => {
    sequenceRef.current = sequence;
  });

  useEffect(() => {
    setTokensError('');
  }, [sequence]);

  const usedDefinitionIds = useMemo(
    () =>
      sequence.reduce<Set<number>>((ids, t) => {
        if (t.definition.type === 'token' && t.definition.value !== 'sample_meta' && _isNil(t.customValue)) {
          ids.add(t.definition.id);
        }
        return ids;
      }, new Set()),
    [sequence]
  );

  const usedMetaGlossaryIds = useMemo(
    () =>
      sequence.reduce<Set<MetadataGlossaryApiId>>((ids, t) => {
        if (t.definition.value === 'sample_meta' && _notNil(t.metadataGlossaryApiId)) {
          ids.add(t.metadataGlossaryApiId);
        }
        return ids;
      }, new Set()),
    [sequence]
  );

  const handleAddToken = useCallback((definition: SampleNameTokenDefinitionV1) => {
    setUserSequence((prev) => [...(prev ?? sequenceRef.current), { localId: newLocalId(), definition }]);
  }, []);

  const handleAddMetaToken = useCallback((definition: SampleNameTokenDefinitionV1, option: SampleMetaOptionV1) => {
    setUserSequence((prev) => [
      ...(prev ?? sequenceRef.current),
      {
        localId: newLocalId(),
        definition,
        metadataGlossaryApiId: option.api_id,
        metadataGlossaryTitle: option.title,
      },
    ]);
  }, []);

  const handleAddFreeText = useCallback(
    (value: string) => {
      const tokenDef = definitions.find((d) => d.type === 'token' && d.value !== 'sample_meta');
      if (_isNil(tokenDef)) {
        return;
      }
      setUserSequence((prev) => [
        ...(prev ?? sequenceRef.current),
        { localId: newLocalId(), definition: tokenDef, customValue: value },
      ]);
    },
    [definitions]
  );

  const handleSeparatorClick = useCallback(
    (def: SampleNameTokenDefinitionV1) => {
      if (autoFill) {
        setUserSequence((prev) => {
          const current = prev ?? sequenceRef.current;
          const nonSeparators = current.filter((t) => t.definition.type !== 'separator');
          return nonSeparators.reduce<Array<SequenceToken>>((acc, token, i) => {
            acc.push(token);
            if (i < nonSeparators.length - 1) {
              acc.push({ localId: newLocalId(), definition: def });
            }
            return acc;
          }, []);
        });
      } else {
        handleAddToken(def);
      }
    },
    [autoFill, handleAddToken]
  );

  const handleRemove = useCallback((localId: string) => {
    setUserSequence((prev) => (prev ?? sequenceRef.current).filter((t) => t.localId !== localId));
  }, []);

  const handleReorder = useCallback((fromIndex: number, toIndex: number) => {
    setUserSequence((prev) => {
      const next = [...(prev ?? sequenceRef.current)];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  const handleTokenChange = useCallback((localId: string, patch: Partial<SequenceToken>) => {
    setUserSequence((prev) =>
      (prev ?? sequenceRef.current).map((t) => (t.localId === localId ? { ...t, ...patch } : t))
    );
  }, []);

  const handleSave = useCallback(async () => {
    if (saving) {
      return;
    }
    if (templateName.trim().length === 0) {
      setNameError('Name is required');
      return;
    }
    if (sequence.length === 0) {
      setTokensError('Add at least one component');
      return;
    }
    if (!hasAnchorToken(sequence)) {
      setTokensError('Require at least one of: Study Code, Sample Type, Animal ID, or custom text');
      return;
    }
    if (hasStudyCodeToken(sequence) && !hasStudyCodes) {
      setTokensError('No study code configured on this tenant. Create a study code, or remove from template');
      return;
    }
    setTokensError('');

    setSaving(true);
    const name = templateName.trim();
    const body = {
      name,
      tokens: sequence.map((t, index) => ({
        position: index,
        element_definition_id: t.definition.id,
        custom_value: t.customValue ?? null,
        metadata_glossary_api_id: t.metadataGlossaryApiId ?? null,
        animal_id_primary: t.animalIdPrimary ?? null,
        animal_id_secondary: t.animalIdSecondary ?? null,
      })),
    };

    try {
      if (isEdit && _notNil(templateApiId)) {
        await updateTemplate({ path: { templateApiId }, body });
        successToast('Template updated');
      } else {
        await createTemplate({ body });
        successToast('Template created');
      }
      onSuccess?.();
      closeModal();
    } catch (cause) {
      ExceptionHandler.captureException(
        new InVivoError(`Could not ${isEdit ? 'update' : 'create'} template`, {
          slug: 'template-builder-save',
          level: 'warning',
          cause,
        })
      );
      errorToast(`Could not ${isEdit ? 'update' : 'create'} template`);
    } finally {
      setSaving(false);
    }
  }, [saving, sequence, templateName, isEdit, templateApiId, createTemplate, updateTemplate, onSuccess, closeModal]);

  const isLoading = definitionsLoading || (isEdit && templateLoading && sequence.length === 0);
  const title = readOnly
    ? _isNotBlank(templateName)
      ? templateName
      : 'Sample name template'
    : isEdit
      ? 'Edit template'
      : 'Create template';

  return (
    <ModalContainer size="medium">
      <ModalHeader title={title} closeModal={closeModal} />
      {isLoading ? (
        <div className="pa4">
          <Loading />
        </div>
      ) : (
        <>
          {!readOnly && (
            <div className={`pa3 b--moon-gray ${styles.toolbar}`}>
              <SelectDropDown title="Add component" className="plain f6" btnProps={{ outline: true }}>
                <TokenMenu
                  definitions={definitions}
                  usedDefinitionIds={usedDefinitionIds}
                  usedMetaGlossaryIds={usedMetaGlossaryIds}
                  onAdd={handleAddToken}
                  onAddMeta={handleAddMetaToken}
                  onAddFreeText={handleAddFreeText}
                />
              </SelectDropDown>
              <div className={styles.separatorSection}>
                <span className={styles.separatorLabel}>Separators:</span>
                <div className={styles.separatorButtons}>
                  {separators.map((def) => (
                    <button
                      key={def.id}
                      type="button"
                      className={styles.separatorBtn}
                      onClick={() => handleSeparatorClick(def)}
                      title={autoFill ? `Auto-fill ${def.label} between all tokens` : def.label}
                    >
                      {def.value}
                    </button>
                  ))}
                </div>
                <div className="flex items-center ml2" style={{ gap: 6 }}>
                  <Switch
                    testId="auto-fill-separator"
                    ariaLabel="Auto-fill separator"
                    value={autoFill}
                    onChange={() => setAutoFill((v) => !v)}
                  />
                  <label
                    className="f6 mb0 mid-gray nowrap"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setAutoFill((v) => !v)}
                  >
                    Auto-fill
                  </label>
                </div>
              </div>
            </div>
          )}
          <div className="pa3 flex flex-column" style={{ gap: 16 }}>
            <TemplateSequenceEditor
              sequence={sequence}
              onRemove={readOnly ? () => {} : handleRemove}
              onReorder={readOnly ? () => {} : handleReorder}
              onTokenChange={readOnly ? undefined : handleTokenChange}
              readOnly={readOnly}
            />
            {!readOnly && _isNotBlank(tokensError) && (
              <p className="f6 red db mt0" data-testid="template-builder-tokens-error">
                {tokensError}
              </p>
            )}
            <TemplatePreview sequence={sequence} />
            {!readOnly && (
              <div className={styles.previewContainer}>
                <label htmlFor="template-name-input" className={styles.previewLabel}>
                  Template name
                </label>
                <input
                  id="template-name-input"
                  type="text"
                  autoComplete="off"
                  value={templateName}
                  onChange={(e) => {
                    setTemplateName(e.target.value);
                    if (_isNotBlank(nameError)) {
                      setNameError('');
                    }
                  }}
                  className={_isNotBlank(nameError) ? 'ui__error' : undefined}
                  style={{ minHeight: '41px' }}
                />
                {_isNotBlank(nameError) && <p className="f6 red db">{nameError}</p>}
              </div>
            )}
          </div>
          {!readOnly && (
            <ModalActions
              className="pa3 bt b--moon-gray w-100"
              onSubmit={handleSave}
              onCancel={closeModal}
              submitBtnText={isEdit ? 'Save' : 'Create'}
              cancelBtnText="Cancel"
              submitButtonProps={{ loading: saving, disabled: saving, testId: 'template-builder-submit' }}
              cancelButtonProps={{ disabled: saving }}
            />
          )}
        </>
      )}
    </ModalContainer>
  );
};
